import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import defaultSoundTicket from "./assets/sound.mp3";
import defaultSoundMessage from "./assets/chat_notify.mp3";
import useSettings from "./hooks/useSettings";

// sons de som customizado (Setting) sao salvos com typeArch=sound, o que
// grava em public/company{id}/sound/ em vez da pasta plana usada pelos
// logos (typeArch=logo) - por isso a URL publica precisa do prefixo
// company{id} aqui, diferente do calculatedLogo* que usa so /public/.
const soundPublicUrl = (file) => {
  if (!file) return null;
  return `${getBackendUrl()}/public/company1/sound/${file}`;
};

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const [soundTicket, setSoundTicket] = useState(defaultSoundTicket);
  const [soundMessage, setSoundMessage] = useState(defaultSoundMessage);
  const [soundAttention, setSoundAttention] = useState(defaultSoundTicket);
  const [soundLogin, setSoundLogin] = useState(defaultSoundTicket);
  const { getPublicSetting } = useSettings();
  // Estado para controlar o prompt de instalação do PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      setSoundTicket,
      setSoundMessage,
      setSoundAttention,
      setSoundLogin,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
            optionsBackground: mode === "light" ? "#fafafa" : "#333",
            fancyBackground: mode === "light" ? "#fafafa" : "#333",
            total: mode === "light" ? "#fff" : "#222",
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : "#333",
            barraSuperior: mode === "light" ? primaryColorLight : "#666",
          },
          shape: {
            borderRadius: 14,
          },
          overrides: {
            MuiPaper: {
              rounded: {
                borderRadius: 14,
              },
              elevation1: {
                boxShadow: mode === "light"
                  ? "0 1px 3px rgba(30, 20, 60, 0.08)"
                  : "0 1px 3px rgba(0, 0, 0, 0.3)",
              },
              elevation2: {
                boxShadow: mode === "light"
                  ? "0 4px 16px rgba(30, 20, 60, 0.10)"
                  : "0 4px 16px rgba(0, 0, 0, 0.35)",
              },
              outlined: {
                borderRadius: 14,
                border: `1px solid ${mode === "light" ? "#E7E4F2" : "#2E294A"}`,
                boxShadow: mode === "light"
                  ? "0 1px 3px rgba(30, 20, 60, 0.06)"
                  : "0 1px 3px rgba(0, 0, 0, 0.25)",
              },
            },
            MuiButton: {
              root: {
                borderRadius: 10,
                textTransform: "none",
              },
              // Botoes "outlined" sem cor (ex: "+ Adicionar ticket") hoje
              // saem cinza/sem graca, o mockup usa um estilo "ghost" com
              // a cor de marca (fundo levemente roxo, borda e texto roxo)
              // pra qualquer acao secundaria.
              outlined: {
                borderColor: mode === "light" ? `${primaryColorLight}55` : `${primaryColorDark}66`,
                color: mode === "light" ? primaryColorLight : primaryColorDark,
                backgroundColor: mode === "light" ? `${primaryColorLight}0F` : `${primaryColorDark}1A`,
                "&:hover": {
                  backgroundColor: mode === "light" ? `${primaryColorLight}1F` : `${primaryColorDark}29`,
                  borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                },
              },
            },
            MuiChip: {
              root: {
                borderRadius: 8,
              },
            },
            MuiAppBar: {
              root: {
                boxShadow: mode === "light"
                  ? "0 1px 3px rgba(30, 20, 60, 0.08)"
                  : "0 1px 3px rgba(0, 0, 0, 0.3)",
              },
            },
            MuiOutlinedInput: {
              root: {
                borderRadius: 10,
              },
              notchedOutline: {
                borderRadius: 10,
              },
            },
            MuiDialog: {
              paper: {
                borderRadius: 16,
              },
            },
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          soundTicket,
          soundMessage,
          soundAttention,
          soundLogin,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, soundTicket, soundMessage, soundAttention, soundLogin, locale, mode, primaryColorDark, primaryColorLight]
  );

  // Detecta quando o navegador está pronto para mostrar o prompt de instalação do PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Previne o comportamento padrão do navegador
      e.preventDefault();
      // Armazena o evento para uso posterior
      setDeferredPrompt(e);
      
      // Mostra o prompt de instalação imediatamente
      setTimeout(() => {
        showInstallPrompt();
      }, 2000); // Pequeno delay para garantir que a página já carregou
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Função para mostrar o prompt de instalação
  const showInstallPrompt = () => {
    if (deferredPrompt) {
      // Verifica se o PWA já está instalado
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        // Mostra o prompt de instalação
        deferredPrompt.prompt();
        
        // Espera pela resposta do usuário
        deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            console.log('Usuário aceitou instalar o app');
          } else {
            console.log('Usuário recusou instalar o app');
          }
          // Limpa o prompt armazenado, só pode ser usado uma vez
          setDeferredPrompt(null);
        });
      }
    }
  };

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    if (!i18nlocale) return;
    const browserLocale = i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    console.log("|=========== handleSaveSetting ==========|")
    console.log("APP START")
    console.log("|========================================|")
   
    
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(file ? getBackendUrl() + "/public/" + file : defaultLogoLight);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(file ? getBackendUrl() + "/public/" + file : defaultLogoDark);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("sound_ticket")
      .then((file) => {
        setSoundTicket(soundPublicUrl(file) || defaultSoundTicket);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("sound_message")
      .then((file) => {
        setSoundMessage(soundPublicUrl(file) || defaultSoundMessage);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("sound_attention")
      .then((file) => {
        setSoundAttention(soundPublicUrl(file) || defaultSoundTicket);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("sound_login")
      .then((file) => {
        setSoundLogin(soundPublicUrl(file) || defaultSoundTicket);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        // Fallback neutro (nunca uma marca especifica) - o Voicechat e'
        // revendido white-label (plano RRStrike), entao um nome de
        // produto fixo aqui vazaria pro tenant errado se essa busca
        // falhar por qualquer motivo.
        setAppName(name || "Sistema");
      })
      .catch((error) => {
        console.log("!==== Erro ao carregar temas: ====!", error);
        setAppName("Sistema");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? getBackendUrl() + "/public/" + appLogoFavicon : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
  <div style={{ position: "relative", overflow: "visible", zIndex: 0, minHeight: "100vh" }}>
    <Routes />
  </div>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;